import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { TAC_RATE } from '../utils/constants';
import { useCruiseSettings, getCruiseDecks } from '../hooks/useCruiseSettings';
import { usePricingLookup } from '../hooks/usePricing';
import { ratesFor, priceLoadFailedMessage, priceMissingMessage, PRICE_LOADING_MESSAGE } from '../utils/pricing';
import { generateBookingReference } from '../utils/bookingReference';
import { getWholeCruiseClosure, closureMessage } from '../utils/closures';
import GuestListEditor, { persistGuestList, hasGuestData } from './GuestListEditor';
import { AlertTriangle, Armchair, Users, ChevronRight } from 'lucide-react';

// Default commission rate as a whole-number percentage (mirrors TAC_RATE, the
// legacy fraction). Used for free-typed partners with no linked tac_rate.
const DEFAULT_TAC_PCT = TAC_RATE * 100;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const formatDateWords = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinal(d)} ${MONTHS[m - 1]} ${y}`;
};

const toInputDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m, y] = ddmmyyyy.split('-');
  return `${y}-${m}-${d}`;
};
const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};

// 0 when the slot has no usable live rate — the caller treats 0 as "leave the
// total alone"; the save guard refuses rather than pricing from a guess.
const calcAmount = (cruise, deck, adults, children, pricing) => {
  const rates = ratesFor(pricing, cruise, deck);
  if (!rates) return 0;
  return (Number(adults) || 0) * rates.adult + (Number(children) || 0) * rates.child;
};

// Custom select — opens reliably on Enter/Space on all browsers and platforms.
// Native <select> cannot be opened programmatically in Safari or Firefox.
function SelectField({ value, onChange, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const listboxId = useRef(`sf-${Math.random().toString(36).slice(2, 7)}`).current;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setActiveIdx(Math.max(options.indexOf(value), 0));
    setOpen(true);
  };

  const pick = (opt) => {
    onChange(opt);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        open && activeIdx >= 0 ? pick(options[activeIdx]) : openMenu();
        break;
      case 'ArrowDown':
        e.preventDefault();
        open ? setActiveIdx((i) => Math.min(i + 1, options.length - 1)) : openMenu();
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) setActiveIdx((i) => Math.max(i - 1, 0));
        break;
      case 'Escape':
        if (open) setOpen(false);
        break;
      case 'Tab':
        if (open) {
          // Forward Tab with a highlighted item → select it, then let Tab move focus naturally.
          // Shift+Tab → just close, no selection, move to previous field.
          if (!e.shiftKey && activeIdx >= 0) onChange(options[activeIdx]);
          setOpen(false);
          // No preventDefault — browser handles focus movement.
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={openMenu}
        onKeyDown={handleKeyDown}
        onFocus={(e) => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.boxShadow = '0 0 0 3px #C9A84C22'; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; e.currentTarget.style.boxShadow = 'none'; }}
        style={{
          width: '100%',
          textAlign: 'left',
          background: disabled ? '#f3f0ea' : '#ffffff',
          border: '1.5px solid var(--color-border-strong)',
          borderRadius: 8,
          padding: '10px 32px 10px 12px',
          color: value ? '#0A1628' : 'var(--color-ink-faint)',
          fontSize: '0.9rem',
          fontWeight: value ? 500 : 400,
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          lineHeight: '1.4',
          position: 'relative',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
      >
        {value || placeholder}
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: '0.7rem', color: disabled ? '#c0b8a8' : 'var(--color-ink-muted)', pointerEvents: 'none',
        }}>▾</span>
      </button>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
            background: '#ffffff', border: '1.5px solid #C9A84C', borderRadius: 9,
            boxShadow: '0 6px 20px rgba(10,22,40,0.14)',
            maxHeight: 220, overflowY: 'auto', marginTop: 3,
          }}
        >
          {options.map((opt, idx) => (
            <div
              key={opt}
              role="option"
              aria-selected={opt === value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt)}
              onMouseEnter={() => setActiveIdx(idx)}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                background: idx === activeIdx ? '#F0E8D4' : opt === value ? '#F8F6F1' : '#ffffff',
                fontWeight: opt === value ? 600 : 400,
                color: '#0A1628',
                borderBottom: idx < options.length - 1 ? '1px solid #f0ebe0' : 'none',
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SummaryRow = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 10 }}>
    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0A1628' }}>{value || '—'}</span>
  </div>
);

export default function AddInquiry({ date, onNavigate, editData, prefillData }) {
  const isEdit = !!editData;
  const { settings: cruiseSettings } = useCruiseSettings();
  // ALL cruises, including paused ones (is_active=false) — staff still take
  // phone enquiries for future dates on paused cruises and record them here.
  // Local to this form only; the shared hook stays active-only for every
  // other screen. If this fetch fails we fall back to cruiseSettings below.
  const [allCruises, setAllCruises] = useState([]);
  useEffect(() => {
    supabase.from('cruise_settings').select('*').order('sort_order')
      .then(({ data }) => setAllCruises(data || []));
  }, []);
  const seed = editData || prefillData || {};

  const [inputDate, setInputDate] = useState(toInputDate(date) || '');
  const [cruise, setCruise] = useState(seed.cruise_type || '');
  const [deck, setDeck] = useState(seed.deck || '');
  const [bookingName, setBookingName] = useState(seed.booking_name || '');
  const [partnerName, setPartnerName] = useState(seed.partner_name || '');
  const [partnerPhone, setPartnerPhone] = useState(seed.partner_phone || '');
  const [guestPhone, setGuestPhone] = useState(seed.guest_phone || seed.phone_number || '');
  const [adults, setAdults] = useState(seed.adults ?? '');
  const [children, setChildren] = useState(seed.children ?? '');
  const [infants, setInfants] = useState(seed.infants ?? 0);
  const [tourLeaders, setTourLeaders] = useState(seed.tour_leaders ?? 0);
  const [showTL, setShowTL] = useState(!!(seed.tour_leaders));
  // Food preferences (Dinner Cruise buffet only). Hidden + saved as 0 otherwise.
  const [foodNonVeg, setFoodNonVeg] = useState(seed.food_non_veg ?? 0);
  const [foodVeg, setFoodVeg]       = useState(seed.food_veg ?? 0);
  const [foodJain, setFoodJain]     = useState(seed.food_jain ?? 0);
  const [totalAmount, setTotalAmount] = useState(seed.total_amount ?? '');
  const [tac, setTac] = useState(seed.tac ?? '');
  // Freeze the discount when EDITING: an existing booking loads with its saved tac
  // treated as authoritative (tacManual=true), so the auto-calc effect never
  // silently recomputes it to the partner's CURRENT rate on load. NEW bookings
  // start unfrozen so selecting a partner auto-fills the discount as before.
  // (Actively re-selecting a partner during an edit unfreezes — see selectPartner.)
  const [tacManual, setTacManual] = useState(isEdit);
  const [paymentStatus, setPaymentStatus] = useState(seed.payment_status || 'Pending');
  const [razorpayAmt, setRazorpayAmt]       = useState(seed.razorpay_amount ?? 0);
  const [cashAmt, setCashAmt]               = useState(seed.cash_amount ?? 0);
  const [iciciAmt, setIciciAmt]             = useState(seed.icici_amount ?? 0);
  const [bankTransferAmt, setBankTransferAmt] = useState(seed.bank_transfer_amount ?? 0);
  const [gpayAmt, setGpayAmt]               = useState(seed.gpay_amount ?? 0);
  const [payWarning, setPayWarning]         = useState('');
  const [payWarningPending, setPayWarningPending] = useState(false);
  const [notes, setNotes] = useState(seed.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [seatingPrompt, setSeatingPrompt] = useState(null); // { bookingId, date, cruise }
  const [existingInvoice, setExistingInvoice] = useState(null); // edit-mode: issued invoice for this booking, if any
  // Optional guest list (ADD mode only — deferred until the booking id exists).
  const [guestsOpen, setGuestsOpen]   = useState(false);
  const [guestSnapshot, setGuestSnapshot] = useState(null); // { primary, additional, cap }
  const [guestWarning, setGuestWarning]   = useState('');   // non-blocking post-create
  const [guestPhoneError, setGuestPhoneError] = useState('');
  const [partnerPhoneError, setPartnerPhoneError] = useState('');
  const [dupCandidates, setDupCandidates] = useState([]);
  const [showDupModal, setShowDupModal] = useState(false);

  const validatePhone = (val) => {
    if (!val || !val.trim()) return '';
    const digits = val.replace(/\D/g, '');
    return digits.length !== 10 ? 'Must be exactly 10 digits' : '';
  };

  const checkDuplicates = async () => {
    if (!bookingName.trim() || !inputDate || !cruise || !deck) return;
    const displayDt = toDisplayDate(inputDate);
    const { data } = await supabase
      .from('bookings')
      .select('id, booking_name, adults, children, partner_name')
      .eq('date', displayDt)
      .eq('cruise_type', cruise)
      .eq('deck', deck)
      .neq('payment_status', 'Cancelled');
    const na = bookingName.trim().toLowerCase().replace(/\s+/g, '');
    const matches = (data || []).filter(b => {
      if (isEdit && b.id === editData.id) return false;
      const nb = (b.booking_name || '').toLowerCase().replace(/\s+/g, '');
      if (!nb) return false;
      if (na === nb || na.includes(nb) || nb.includes(na)) return true;
      const chars = new Set([...na]);
      const overlap = [...chars].filter(c => nb.includes(c)).length;
      return overlap / Math.max(na.length, nb.length) >= 0.8;
    });
    if (matches.length > 0) {
      setDupCandidates(matches);
      setShowDupModal(true);
    }
  };

  // Partners autocomplete
  const [partners, setPartners] = useState([]);
  const [showPartnerDrop, setShowPartnerDrop] = useState(false);
  const [partnerActiveIdx, setPartnerActiveIdx] = useState(-1);
  const [partnerSaved, setPartnerSaved] = useState(false);
  const partnerRef = useRef(null);

  // Partner-aware linkage (Phase 3 Path B). selectedPartnerId is the real FK to
  // partners.id when a partner was picked from the list (null for a free-typed /
  // quick-added name). selectedPartnerRate is that partner's tac_rate (%) for the
  // discount calc. agents = the selected partner's active agents; agentRequired
  // gates the "must pick an agent" validation (only when the link was made in the
  // UI or the edited booking already carried an agent — old bookings stay lenient).
  const [selectedPartnerId, setSelectedPartnerId] = useState(seed.partner_id || null);
  const [selectedPartnerRate, setSelectedPartnerRate] = useState(seed.tac_rate != null ? Number(seed.tac_rate) : null);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(seed.agent_id || '');
  const [agentRequired, setAgentRequired] = useState(false);
  const editMatchDone = useRef(false);

  // Live pricing from the `pricing` table (hooks/usePricing.js) — the whole
  // table, since cruise and deck are chosen on this form. No hardcoded initial
  // state and no fallback: while loading there is simply no rate yet, and a
  // failed read or a missing row for the chosen slot refuses the save below.
  const { lookup: pricingLookup, status: pricingStatus, retry: retryPricing } = usePricingLookup();

  useEffect(() => {
    // Archived partners are hidden from the picker and the quick-add duplicate check.
    supabase.from('partners').select('id, name, phone_number, preferred_payment_status, tac_rate').is('archived_at', null).order('name')
      .then(({ data, error: partnersErr }) => { if (!partnersErr) setPartners(data || []); });
  }, []);

  // Close partner dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (partnerRef.current && !partnerRef.current.contains(e.target)) {
        setShowPartnerDrop(false);
        setPartnerActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredPartners = partnerName.trim()
    ? partners.filter((p) => p.name.toLowerCase().includes(partnerName.trim().toLowerCase()))
    : partners;

  const selectPartner = (p) => {
    setPartnerName(p.name);
    if (p.phone_number) setPartnerPhone(p.phone_number);
    if (p.preferred_payment_status) setPaymentStatus(p.preferred_payment_status);
    // Capture the real partner id + rate for the discount calc, and reset the
    // agent selection (a fresh partner means the previous agent no longer applies).
    setSelectedPartnerId(p.id);
    setSelectedPartnerRate(p.tac_rate != null ? Number(p.tac_rate) : null);
    setSelectedAgentId('');
    setAgentRequired(true);
    // Picking a partner is a deliberate action — unfreeze so the discount
    // auto-fills from THIS partner's current rate (incl. re-selecting mid-edit).
    setTacManual(false);
    setShowPartnerDrop(false);
    setPartnerActiveIdx(-1);
  };

  const handlePartnerKeyDown = (e) => {
    if (e.key === 'Tab') {
      // Forward Tab with a highlighted partner → select them, let Tab move focus.
      // Shift+Tab → just close dropdown and move to previous field.
      if (!e.shiftKey && partnerActiveIdx >= 0 && showPartnerDrop && filteredPartners.length > 0) {
        selectPartner(filteredPartners[partnerActiveIdx]);
      } else {
        setShowPartnerDrop(false);
        setPartnerActiveIdx(-1);
      }
      // No preventDefault — browser handles focus movement.
      return;
    }
    if (!showPartnerDrop || !filteredPartners.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPartnerActiveIdx((i) => Math.min(i + 1, filteredPartners.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPartnerActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && partnerActiveIdx >= 0) {
      e.preventDefault();
      selectPartner(filteredPartners[partnerActiveIdx]);
    } else if (e.key === 'Escape') {
      setShowPartnerDrop(false);
      setPartnerActiveIdx(-1);
    }
  };

  const isNewPartner = partnerName.trim() &&
    !partners.some((p) => p.name.toLowerCase() === partnerName.trim().toLowerCase());

  const handleSavePartner = async () => {
    const res = await supabase.from('partners').insert({
      name: partnerName.trim(),
      phone_number: partnerPhone.trim() || null,
    }).select('id, name, phone_number, tac_rate').single();
    if (!res.error && res.data) {
      setPartners((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setPartnerSaved(true);
      setTimeout(() => setPartnerSaved(false), 2000);
    }
  };

  // Cruise selection works off cruiseList (active + paused) so paused cruises
  // are selectable and an edit of a paused-cruise enquiry still resolves.
  // Paused cruises get a " (Paused)" suffix in the DISPLAY label only —
  // `cruise` state always holds the plain cruise_name.
  const cruiseList = allCruises.length ? allCruises : cruiseSettings;
  const toLabel = (name) => {
    const s = cruiseList.find(c => c.cruise_name === name);
    return s && s.is_active === false ? `${name} (Paused)` : name;
  };
  const fromLabel = (label) => (label || '').replace(/ \(Paused\)$/, '');
  const cruiseOptions = cruiseList.map(s => toLabel(s.cruise_name));
  const currentCruiseSetting = cruiseList.find(s => s.cruise_name === cruise);
  const availableDecks = currentCruiseSetting ? getCruiseDecks(currentCruiseSetting) : [];

  // Auto-calc total
  useEffect(() => {
    if (!isEdit || (cruise && deck)) {
      const auto = calcAmount(cruise, deck, adults, children, pricingLookup);
      if (auto > 0 || (adults === '' && children === '')) {
        setTotalAmount(auto === 0 ? '' : auto);
      }
    }
  }, [cruise, deck, adults, children, isEdit, pricingLookup]);

  // The chosen slot's validated rates, and one status folding the table read
  // and the slot lookup together:
  //   'loading' / 'error' — the table itself (unreadable or empty)
  //   'idle'              — cruise or deck not chosen yet
  //   'missing'           — table fine, but no usable row for this cruise + deck
  //   'ok'                — slotRates = { adult, child }
  const slotRates = ratesFor(pricingLookup, cruise, deck);
  const priceStatus = pricingStatus !== 'ok' ? pricingStatus
    : (!cruise || !deck) ? 'idle'
    : slotRates ? 'ok' : 'missing';
  // Does this save depend on the price? A new booking always does. An edit
  // only does when a price-affecting field changed — correcting a phone
  // number on an existing booking must not be blocked by a pricing outage.
  // FOC never does: its total is zero regardless of the rate card.
  const priceFieldsChanged = !isEdit
    || String(adults) !== String(seed.adults ?? '')
    || String(children) !== String(seed.children ?? '')
    || cruise !== (seed.cruise_type || '')
    || deck !== (seed.deck || '');
  const priceBlocksSave = paymentStatus !== 'FOC' && priceFieldsChanged
    && (priceStatus === 'loading' || priceStatus === 'error' || priceStatus === 'missing');

  // Auto-calc TAC when partner / total changes (unless manually edited). A partner
  // picked from the list (selectedPartnerId set) uses THEIR tac_rate; a free-typed
  // partner falls back to the default flat rate. Manual edits still stick.
  useEffect(() => {
    if (!tacManual) {
      const total = Number(totalAmount) || 0;
      if (!partnerName.trim()) { setTac(0); return; }
      const pct = (selectedPartnerId && selectedPartnerRate != null) ? selectedPartnerRate : DEFAULT_TAC_PCT;
      setTac(Math.round(total * (pct / 100)));
    }
  }, [partnerName, totalAmount, tacManual, selectedPartnerId, selectedPartnerRate]);

  // Reset TAC manual flag when partner name clears
  useEffect(() => {
    if (!partnerName.trim()) setTacManual(false);
  }, [partnerName]);

  // Load the selected partner's active agents (for the agent dropdown). Clears
  // when no partner is linked.
  useEffect(() => {
    if (!selectedPartnerId) { setAgents([]); return; }
    let cancelled = false;
    (async () => {
      const { data, error: agentsErr } = await supabase
        .from('partner_agents')
        .select('id, agent_name, agent_phone')
        .eq('partner_id', selectedPartnerId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (!cancelled && !agentsErr) setAgents(data || []);
    })();
    return () => { cancelled = true; };
  }, [selectedPartnerId]);

  // Edit-mode guardrail: if this booking already has an issued invoice, surface a
  // warning banner (invoices are fixed records — correct via credit note, not edits).
  useEffect(() => {
    if (!isEdit || !seed?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('booking_id', seed.id)
        .maybeSingle();
      if (!cancelled && data) setExistingInvoice(data);
    })();
    return () => { cancelled = true; };
  }, [isEdit, seed?.id]);

  // Edit-mode matching (runs once, after partners load). If the booking has a
  // partner_id use it; otherwise try a case-insensitive exact name match against
  // an existing partner. Enrich the rate from the matched partner. Old bookings
  // that had no agent stay lenient (agentRequired=false) — no new forced pick.
  useEffect(() => {
    if (!isEdit || editMatchDone.current) return;
    if (!partners.length) return;
    let match = null;
    if (seed.partner_id) match = partners.find((p) => p.id === seed.partner_id) || null;
    if (!match && seed.partner_name) {
      match = partners.find((p) => p.name.trim().toLowerCase() === seed.partner_name.trim().toLowerCase()) || null;
    }
    if (match) {
      setSelectedPartnerId(match.id);
      // Preserve the booking's ORIGINAL saved tac_rate on edit — don't replace it
      // with the partner's current rate. Only fall back to the matched partner's
      // rate for old bookings that predate this field (no saved tac_rate).
      setSelectedPartnerRate(
        seed.tac_rate != null ? Number(seed.tac_rate) : (match.tac_rate != null ? Number(match.tac_rate) : null)
      );
      setAgentRequired(!!seed.agent_id);
    }
    editMatchDone.current = true;
  }, [isEdit, partners]); // eslint-disable-line react-hooks/exhaustive-deps

  const netTotal = (Number(totalAmount) || 0) - (Number(tac) || 0);

  // The TAC rate (%) actually applied — saved to bookings.tac_rate. Null when no
  // partner is set at all; the selected partner's rate when linked, else default.
  const effectiveTacRate = partnerName.trim()
    ? ((selectedPartnerId && selectedPartnerRate != null) ? selectedPartnerRate : DEFAULT_TAC_PCT)
    : null;

  // Agent dropdown (SelectField takes string options; map label <-> id by index).
  // Labelled "Partner – Agent" per spec.
  const agentOptions = agents.map((a) => `${partnerName.trim()} – ${a.agent_name}`);
  const selectedAgentIdx = agents.findIndex((a) => a.id === selectedAgentId);
  const selectedAgentLabel = selectedAgentIdx >= 0 ? agentOptions[selectedAgentIdx] : '';
  const handleAgentPick = (label) => {
    const idx = agentOptions.indexOf(label);
    const agent = idx >= 0 ? agents[idx] : null;
    setSelectedAgentId(agent ? agent.id : '');
    // Route the ticket to the specific agent: override partner_phone with the
    // agent's number (agent is more specific than the partner's general phone).
    // Guard: if the agent somehow has no phone, keep the existing partner phone
    // rather than blanking it. Field stays manually editable afterwards.
    if (agent && agent.agent_phone) {
      setPartnerPhone(agent.agent_phone);
      setPartnerPhoneError('');
    }
  };
  const showAgentField = !!selectedPartnerId && agents.length > 0;

  // Food preferences apply ONLY to the Dinner Cruise; must total adults + children
  // (tour leaders and infants are excluded).
  const isDinnerCruise = (cruise || '').toLowerCase().includes('dinner');
  const foodRequired = (Number(adults) || 0) + (Number(children) || 0);
  const foodTotal = (Number(foodNonVeg) || 0) + (Number(foodVeg) || 0) + (Number(foodJain) || 0);
  const foodValid = !isDinnerCruise || foodTotal === foodRequired;

  const handleCruiseChange = (val) => {
    const name = fromLabel(val); // dropdown label may carry the (Paused) suffix
    setCruise(name);
    const setting = cruiseList.find(s => s.cruise_name === name);
    // Auto-set deck when only one option is available
    const decks = setting ? getCruiseDecks(setting) : [];
    setDeck(decks.length === 1 ? decks[0] : '');
  };

  const executeSave = async () => {
    setPayWarningPending(false);
    setPayWarning('');
    setSaving(true);
    // Block creating a NEW booking when the whole cruise is closed online for this
    // date (deck-only closures don't block here). Editing an existing booking is
    // not blocked. Private Charter has its own flow and is never gated.
    if (!isEdit) {
      const closure = await getWholeCruiseClosure(cruise, toDisplayDate(inputDate));
      if (closure) { setSaving(false); setError(closureMessage(closure, toDisplayDate(inputDate))); return; }
    }
    const payload = {
      date: toDisplayDate(inputDate),
      cruise_type: cruise,
      deck,
      booking_name: bookingName.trim(),
      partner_name: partnerName.trim() || null,
      partner_phone: partnerPhone.trim() || null,
      guest_phone: guestPhone.trim() || null,
      adults: Number(adults),
      children: Number(children) || 0,
      infants: Number(infants) || 0,
      tour_leaders: Number(tourLeaders) || 0,
      food_non_veg: isDinnerCruise ? (Number(foodNonVeg) || 0) : 0,
      food_veg:     isDinnerCruise ? (Number(foodVeg) || 0) : 0,
      food_jain:    isDinnerCruise ? (Number(foodJain) || 0) : 0,
      total_amount: Number(totalAmount) || 0,
      tac: Number(tac) || 0,
      net_total: netTotal,
      // Partner-aware fields (additive — legacy partner_name/partner_phone/tac kept above).
      partner_id: selectedPartnerId || null,
      agent_id: selectedAgentId || null,
      tac_rate: effectiveTacRate,
      tac_amount: Number(tac) || 0,
      booking_channel: 'internal',
      // Online-lead linkage: set only when this form was opened from the
      // Incomplete Bookings screen (prefill carries the lead's customer_id).
      // Null for every other entry point. Ties the booking to the online
      // customer so the lead drops off the Incomplete list and duplicate
      // online bookings are detectable by exact id, not phone heuristics.
      customer_id: seed.customer_id || null,
      payment_status: paymentStatus || null,
      razorpay_amount: Number(razorpayAmt) || 0,
      cash_amount: Number(cashAmt) || 0,
      icici_amount: Number(iciciAmt) || 0,
      bank_transfer_amount: Number(bankTransferAmt) || 0,
      gpay_amount: Number(gpayAmt) || 0,
      notes: notes.trim() || null,
    };

    let result;
    if (isEdit) {
      result = await supabase.from('bookings').update(payload).eq('id', editData.id);
    } else {
      result = await supabase.from('bookings').insert(payload).select('id').single();
    }

    setSaving(false);
    if (result.error) {
      setError(result.error.message);
    } else if (isEdit) {
      setSuccess('Booking updated!');
      setTimeout(() => onNavigate(seed._return_to || 'view-bookings', toDisplayDate(inputDate)), 1000);
    } else {
      setSuccess('Booking saved!');
      const bookingId = result.data.id;
      const cruiseCode = currentCruiseSetting?.cruise_code;
      if (cruiseCode) {
        generateBookingReference(bookingId, cruiseCode, toDisplayDate(inputDate)).catch(() => {});
      }
      // Deferred guest list: now that the booking id exists, persist any guests
      // the user entered. The booking already succeeded — a guest failure is
      // surfaced as a non-blocking warning and never rolls back the booking.
      setGuestWarning('');
      if (hasGuestData(guestSnapshot)) {
        const res = await persistGuestList(bookingId, {
          primaryName: bookingName.trim(),
          primary: guestSnapshot.primary,
          additional: guestSnapshot.additional,
          cap: guestSnapshot.cap,
        });
        if (res.error) {
          setGuestWarning(`Booking saved, but the guest list could not be saved (${res.error}). You can add it later from View Bookings.`);
        }
      }
      // Enquiry conversion: mark the source enquiry Handled and record which
      // booking it became. Non-fatal — the booking is already saved — but
      // never silent: an unmarked enquiry looks untouched and invites a
      // second conversion, so a failure surfaces as a visible warning.
      let enquiryWarn = '';
      if (seed._enquiry_id) {
        const { error: enqErr } = await supabase
          .from('online_enquiries')
          .update({ status: 'Handled', converted_booking_id: bookingId })
          .eq('id', seed._enquiry_id);
        if (enqErr) {
          enquiryWarn = `Booking saved, but the enquiry could not be marked as Handled (${enqErr.message}). Mark it by hand on the Enquiries screen so it isn't converted twice.`;
          setGuestWarning(prev => prev ? `${prev} ${enquiryWarn}` : enquiryWarn);
          setError(enquiryWarn);
        }
      }
      if (currentCruiseSetting?.is_active === false) {
        // Paused cruise: it has no seating sheet, so skip the assign-a-table
        // prompt — the enquiry just saves. Leave like a completed save, with
        // the same navigation the prompt's "Assign Later" button uses.
        const returnTo = seed._return_to || 'view-bookings';
        // Give a visible enquiry-update warning time to be read before leaving.
        setTimeout(() => {
          if (returnTo === 'daily-tracker') {
            onNavigate('daily-tracker', toDisplayDate(inputDate), { cruise_type: cruise || seed.cruise_type });
          } else {
            onNavigate(returnTo, toDisplayDate(inputDate));
          }
        }, enquiryWarn ? 4000 : 1000);
      } else {
        setSeatingPrompt({
          bookingId,
          date: toDisplayDate(inputDate),
          cruise,
          returnTo: seed._return_to || 'view-bookings',
          returnCruise: cruise || seed.cruise_type,
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (payWarningPending) return;
    setError('');
    setSuccess('');

    if (!inputDate || !cruise || !deck || !bookingName || !adults) {
      setError('Please fill all required fields.');
      return;
    }

    // Hard stop: no live price, no booking (see priceBlocksSave). A manually
    // typed total does not bypass it; FOC and price-neutral edits are exempt.
    if (priceBlocksSave) {
      setError(priceStatus === 'loading' ? PRICE_LOADING_MESSAGE
        : priceStatus === 'missing' ? priceMissingMessage(cruise, deck)
        : priceLoadFailedMessage(cruise, deck));
      return;
    }

    const gpErr = validatePhone(guestPhone);
    const ppErr = validatePhone(partnerPhone);
    if (gpErr) { setGuestPhoneError(gpErr); setError('Please fix phone number errors.'); return; }
    if (ppErr) { setPartnerPhoneError(ppErr); setError('Please fix phone number errors.'); return; }

    // Partner-aware: if a linked partner has agents and the link was user-made
    // (or the booking already had an agent), staff must pick one.
    if (selectedPartnerId && agents.length > 0 && agentRequired && !selectedAgentId) {
      setError('Please select an agent for this partner.');
      return;
    }

    // Dinner Cruise: food preferences must total adults + children.
    if (isDinnerCruise && foodTotal !== foodRequired) {
      setError(`Food preferences must total ${foodRequired} (adults + children).`);
      return;
    }

    // Payment warning — block save until user acknowledges
    if (paymentStatus === 'Confirmed') {
      const paySum = (Number(razorpayAmt)||0) + (Number(cashAmt)||0) + (Number(iciciAmt)||0) + (Number(bankTransferAmt)||0) + (Number(gpayAmt)||0);
      if (paySum !== netTotal) {
        setPayWarning(`Payment amounts (₹${paySum.toLocaleString('en-IN')}) don't match Net Total (₹${netTotal.toLocaleString('en-IN')}).`);
        setPayWarningPending(true);
        return;
      }
    }
    setPayWarning('');
    await executeSave();
  };

  // Summary panel data
  const paxParts = [];
  if (Number(adults) > 0) paxParts.push(`${adults} Adults`);
  if (Number(children) > 0) paxParts.push(`${children} Children`);
  if (Number(infants) > 0) paxParts.push(`${infants} Infants`);
  if (Number(tourLeaders) > 0) paxParts.push(`${tourLeaders} TL`);
  const paxStr = paxParts.length ? paxParts.join(' + ') : '—';
  const netTotalFmt = netTotal ? `₹${netTotal.toLocaleString('en-IN')}` : '—';

  // Mobile summary bar compact values
  const barDate = inputDate
    ? `${Number(inputDate.split('-')[2])} ${MONTHS[Number(inputDate.split('-')[1]) - 1].slice(0, 3)}`
    : '—';
  const cruiseShort = cruise ? cruise.split(' ')[0] : '—';
  const deckShort = deck ? deck.replace(' Deck', '') : '—';

  return (
    <div>
      {/* Mobile summary bar */}
      <div className="inquiry-summary-bar">
        {/* Row 1: Date, Cruise, Deck */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <div style={{ fontSize: '0.6rem', color: '#7090b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Date</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#ffffff' }}>{barDate}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.6rem', color: '#7090b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Cruise</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: cruise ? '#c8d8f0' : '#4a5a70' }}>{cruiseShort}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.6rem', color: '#7090b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Deck</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: deck ? '#c8d8f0' : '#4a5a70' }}>{deckShort}</div>
          </div>
        </div>
        {/* Row 2: Pax and Total */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '0.6rem', color: '#7090b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Pax</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#ffffff' }}>{paxStr}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.6rem', color: '#7090b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Net Total</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#C9A84C' }}>{netTotalFmt}</div>
          </div>
        </div>
      </div>

      <div className="back-row">
        <button className="back-btn" onClick={() => {
          if (seed._return_to === 'daily-tracker') {
            onNavigate('daily-tracker', toDisplayDate(inputDate), { cruise_type: cruise || seed.cruise_type });
          } else {
            onNavigate(seed._return_to || 'home', toDisplayDate(inputDate));
          }
        }}>
          ← Back
        </button>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#0A1628' }}>
          {isEdit ? 'Edit Booking' : 'Add Booking'}
        </h2>
      </div>

      <div className="inquiry-layout inquiry-form-padded">
        {/* Form */}
        <div className="inquiry-form-main">
          <div className="form-card">
            {pricingStatus === 'error' && (
              <div className="toast toast-error" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1 }}>Pricing couldn't be loaded. Bookings can't be saved until it does (FOC bookings and edits that don't change pax, cruise or deck can).</span>
                <button type="button" className="btn btn-outline btn-sm" onClick={retryPricing} style={{ flexShrink: 0 }}>Retry</button>
              </div>
            )}
            {priceStatus === 'missing' && (
              <div className="toast toast-error">
                No price is set for {cruise} · {deck} in the Pricing screen. Bookings for it can't be saved until one is (FOC can).
              </div>
            )}
            {error && <div className="toast toast-error">{error}</div>}
            {success && <div className="toast toast-success">{success}</div>}

            {existingInvoice && (
              <div style={{
                background: '#FEF3E2', border: '1px solid #E9B949', borderRadius: 8,
                padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <AlertTriangle size={18} color="#B7791F" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 13, color: '#7A5B15', lineHeight: 1.5 }}>
                  <strong>This booking has an issued invoice ({existingInvoice.invoice_number}).</strong> The invoice is a fixed record and won't change if you edit this booking. To correct an invoiced booking, issue a credit note.
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">

                {/* Date */}
                <div className="form-group">
                  <label>Date <span className="required">*</span></label>
                  <input
                    type="date"
                    value={inputDate}
                    onChange={(e) => setInputDate(e.target.value)}
                    style={{ colorScheme: 'light' }}
                    autoFocus
                    required
                  />
                  {inputDate && (
                    <span style={{ display: 'block', marginTop: 6, fontSize: '0.85rem', fontWeight: 600, color: '#000000' }}>
                      {formatDateWords(inputDate)}
                    </span>
                  )}
                </div>

                {/* Cruise Type */}
                <div className="form-group">
                  <label>Cruise Type <span className="required">*</span></label>
                  <SelectField
                    value={toLabel(cruise)}
                    onChange={handleCruiseChange}
                    options={cruiseOptions}
                    placeholder="Select cruise…"
                  />
                </div>

                {/* Deck */}
                <div className="form-group">
                  <label>Deck <span className="required">*</span></label>
                  <SelectField
                    value={deck}
                    onChange={setDeck}
                    options={availableDecks}
                    placeholder="Select deck…"
                    disabled={!cruise}
                  />
                </div>

                {/* Adults */}
                <div className="form-group">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ margin: 0 }}>Adults <span className="required">*</span></label>
                    {!showTL && (
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowTL(true)}
                        style={{
                          background: 'none', border: 'none', color: '#C9A84C',
                          fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                          padding: '2px 0', lineHeight: 1, flexShrink: 0,
                        }}
                      >
                        + Add TL
                      </button>
                    )}
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={adults}
                    onChange={(e) => setAdults(e.target.value)}
                    placeholder="0"
                    required
                  />
                </div>

                {/* Children */}
                <div className="form-group">
                  <label>Children</label>
                  <input
                    type="number"
                    min="0"
                    value={children}
                    onChange={(e) => setChildren(e.target.value)}
                    placeholder="0"
                  />
                </div>

                {/* Infants */}
                <div className="form-group">
                  <label>Infants <span style={{ fontSize:'0.72rem', color:'var(--color-ink-muted)', fontWeight:400 }}>(free)</span></label>
                  <input
                    type="number"
                    min="0"
                    value={infants}
                    onChange={(e) => setInfants(e.target.value)}
                    placeholder="0"
                  />
                </div>

                {/* Food preferences — Dinner Cruise buffet only */}
                {isDinnerCruise && (
                  <div className="form-group full-width">
                    <label>Food preferences (buffet) <span className="required">*</span></label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                      <div className="form-group">
                        <label style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Non-veg</label>
                        <input type="number" min="0" value={foodNonVeg} onChange={(e) => setFoodNonVeg(e.target.value)} placeholder="0" />
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Veg</label>
                        <input type="number" min="0" value={foodVeg} onChange={(e) => setFoodVeg(e.target.value)} placeholder="0" />
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Jain</label>
                        <input type="number" min="0" value={foodJain} onChange={(e) => setFoodJain(e.target.value)} placeholder="0" />
                      </div>
                    </div>
                    <div style={{ marginTop: 6, fontSize: '0.78rem', fontWeight: 600, color: foodValid ? 'var(--color-ink-muted)' : '#dc2626' }}>
                      {foodValid
                        ? `Total ${foodTotal} of ${foodRequired} (adults + children).`
                        : `Food preferences must total ${foodRequired} (adults + children). Currently ${foodTotal}.`}
                    </div>
                  </div>
                )}

                {/* Booking Name */}
                <div className="form-group">
                  <label>Booking Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={bookingName}
                    onChange={(e) => setBookingName(e.target.value)}
                    onBlur={checkDuplicates}
                    placeholder="Guest / company name"
                    required
                  />
                </div>

                {/* Partner Name with autocomplete */}
                <div className="form-group" style={{ position: 'relative' }} ref={partnerRef}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <label style={{ margin: 0 }}>Partner Name</label>
                    {isNewPartner && (
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={handleSavePartner}
                        style={{
                          background: 'none', border: 'none',
                          color: partnerSaved ? '#16a34a' : '#C9A84C',
                          fontSize: '0.75rem', fontWeight: 700,
                          cursor: 'pointer', padding: '2px 0', lineHeight: 1, flexShrink: 0,
                        }}
                      >
                        {partnerSaved ? '✓ Partner saved!' : '+ Save Partner'}
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={partnerName}
                    onChange={(e) => {
                      setPartnerName(e.target.value);
                      // Editing the name breaks the id link until they pick again.
                      setSelectedPartnerId(null);
                      setSelectedPartnerRate(null);
                      setSelectedAgentId('');
                      setAgents([]);
                      setAgentRequired(false);
                      setShowPartnerDrop(true);
                      setPartnerActiveIdx(-1);
                    }}
                    onFocus={() => setShowPartnerDrop(true)}
                    onKeyDown={handlePartnerKeyDown}
                    placeholder="Travel agent (optional)"
                    autoComplete="off"
                  />
                  {showPartnerDrop && filteredPartners.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                      background: '#ffffff', border: '1.5px solid #C9A84C', borderRadius: 9,
                      boxShadow: '0 6px 20px rgba(10,22,40,0.14)', maxHeight: 200, overflowY: 'auto', marginTop: 3,
                    }}>
                      {filteredPartners.map((p, idx) => (
                        <div
                          key={p.id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectPartner(p)}
                          onMouseEnter={() => setPartnerActiveIdx(idx)}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--color-border-subtle)',
                            fontSize: '0.9rem',
                            background: idx === partnerActiveIdx ? '#F0E8D4' : '#ffffff',
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#0A1628' }}>{p.name}</div>
                          {p.phone_number && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginTop: 1 }}>{p.phone_number}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Guest Phone */}
                <div className="form-group">
                  <label>Guest Phone</label>
                  <input
                    type="text"
                    value={guestPhone}
                    onChange={(e) => { setGuestPhone(e.target.value); setGuestPhoneError(''); }}
                    onBlur={(e) => setGuestPhoneError(validatePhone(e.target.value))}
                    placeholder="+91…"
                  />
                  {guestPhoneError && <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>{guestPhoneError}</span>}
                </div>

                {/* Partner Phone */}
                <div className="form-group">
                  <label>Partner Phone</label>
                  <input
                    type="text"
                    value={partnerPhone}
                    onChange={(e) => { setPartnerPhone(e.target.value); setPartnerPhoneError(''); }}
                    onBlur={(e) => setPartnerPhoneError(validatePhone(e.target.value))}
                    placeholder="+91…"
                  />
                  {partnerPhoneError && <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>{partnerPhoneError}</span>}
                </div>

                {/* Agent — only when a partner is linked AND has active agents.
                    Required (when enforced) so staff attribute the booking. */}
                {showAgentField && (
                  <div className="form-group">
                    <label>Agent{agentRequired && <span style={{ color: '#dc2626' }}> *</span>}</label>
                    <SelectField
                      value={selectedAgentLabel}
                      onChange={handleAgentPick}
                      options={agentOptions}
                      placeholder="Select agent…"
                    />
                  </div>
                )}

                {/* Tour Leaders — conditionally shown */}
                {showTL && (
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label style={{ margin: 0 }}>Tour Leaders (FOC)</label>
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => { setShowTL(false); setTourLeaders(0); }}
                        style={{
                          background: 'none', border: 'none', color: 'var(--color-ink-muted)',
                          fontSize: '0.8rem', cursor: 'pointer', padding: '2px 0', lineHeight: 1,
                        }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={tourLeaders}
                      onChange={(e) => setTourLeaders(e.target.value)}
                      placeholder="0"
                    />
                    <span className="calc-note">Free of charge · not counted in capacity</span>
                  </div>
                )}

                {/* Total Amount */}
                <div className="form-group">
                  <label>Total Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={totalAmount}
                    onChange={(e) => setTotalAmount(e.target.value)}
                    placeholder="Auto-calculated"
                  />
                  {priceStatus === 'ok' && (
                    <span className="calc-note">
                      Auto: {adults || 0}×₹{slotRates.adult} + {children || 0}×₹{slotRates.child}
                    </span>
                  )}
                  {priceStatus === 'loading' && <span className="calc-note">Loading price…</span>}
                  {priceStatus === 'missing' && <span className="calc-note" style={{ color: '#dc2626' }}>No price set for this cruise and deck</span>}
                  {priceStatus === 'error' && <span className="calc-note" style={{ color: '#dc2626' }}>Price unavailable</span>}
                </div>

                {/* TAC */}
                <div className="form-group">
                  <label>TAC (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={tac}
                    onChange={(e) => { setTac(e.target.value); setTacManual(true); }}
                    placeholder="0"
                  />
                  <span className="calc-note">
                    {partnerName.trim()
                      ? `${effectiveTacRate}% of Total${selectedPartnerId ? ' (partner rate)' : ''}`
                      : 'Applied when Partner Name set'}
                  </span>
                </div>

                {/* Net Total */}
                <div className="form-group">
                  <label>Net Total (₹)</label>
                  <input
                    type="number"
                    value={isNaN(netTotal) ? '' : netTotal}
                    readOnly
                    tabIndex={-1}
                    style={{ color: '#7a5a10', fontWeight: 700 }}
                  />
                  <span className="calc-note">Total − TAC (not editable)</span>
                </div>

                {/* Payment Status */}
                <div className="form-group">
                  <label>Payment Status</label>
                  <SelectField
                    value={paymentStatus}
                    onChange={(v) => { setPaymentStatus(v); setPayWarning(''); }}
                    options={['Confirmed', 'Pending', 'Balance', 'FOC', 'Cancelled']}
                    placeholder="Select…"
                  />
                </div>

                {/* Payment Breakdown — visible for Confirmed or Balance */}
                {(paymentStatus === 'Confirmed' || paymentStatus === 'Balance') && (
                  <div className="form-group full-width">
                    <div className="pay-section-header">Payment Breakdown</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', width: '100%' }}>
                      {[
                        ['Razorpay', razorpayAmt, setRazorpayAmt],
                        ['Cash', cashAmt, setCashAmt],
                        ['ICICI POS', iciciAmt, setIciciAmt],
                        ['Bank Transfer', bankTransferAmt, setBankTransferAmt],
                      ].map(([label, val, setter]) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div className="pay-field-label">{label}</div>
                          <div className="pay-input-row">
                            <span className="pay-prefix">₹</span>
                            <input
                              type="number"
                              min="0"
                              value={val}
                              onChange={(e) => { setter(e.target.value); setPayWarning(''); }}
                              placeholder="0"
                              style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                          </div>
                        </div>
                      ))}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: 'span 2', maxWidth: '50%' }}>
                        <div className="pay-field-label">GPay</div>
                        <div className="pay-input-row">
                          <span className="pay-prefix">₹</span>
                          <input
                            type="number"
                            min="0"
                            value={gpayAmt}
                            onChange={(e) => { setGpayAmt(e.target.value); setPayWarning(''); }}
                            placeholder="0"
                            style={{ width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional notes…"
                  />
                </div>
              </div>

              {/* Optional guest list — ADD mode only; collapsed by default. Kept
                  mounted (display toggle) so the in-memory list survives collapse;
                  saved with the booking once its id exists. */}
              {!isEdit && (
                <div style={{ marginTop: 18, borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => setGuestsOpen(v => !v)}
                    aria-expanded={guestsOpen}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontSize: '0.82rem', fontWeight: 700, color: '#2a3a5a' }}
                  >
                    <ChevronRight size={15} strokeWidth={2} aria-hidden="true" style={{ transform: guestsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                    <Users size={15} strokeWidth={1.75} aria-hidden="true" />
                    Guest list (optional)
                  </button>
                  <div style={{ display: guestsOpen ? 'block' : 'none', marginTop: 12, background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, padding: '14px 16px' }}>
                    <GuestListEditor
                      deferred
                      primaryName={bookingName}
                      adults={adults}
                      children={children}
                      onChange={setGuestSnapshot}
                    />
                  </div>
                </div>
              )}

              <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
                <button type="submit" className="btn btn-gold" disabled={saving || priceBlocksSave}>
                  {saving ? 'Saving…' : isEdit ? 'Update Booking' : 'Save Booking'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => onNavigate('home', toDisplayDate(inputDate))}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Desktop summary sidebar */}
        <div className="inquiry-summary-sidebar">
          <div style={{
            background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: 14,
            padding: '18px 16px', boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
          }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14 }}>
              Summary
            </div>
            <SummaryRow label="Date" value={inputDate ? formatDateWords(inputDate) : null} />
            <SummaryRow label="Cruise" value={cruise} />
            <SummaryRow label="Deck" value={deck} />
            <SummaryRow label="Pax" value={paxStr !== '—' ? paxStr : null} />
            <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 4, paddingTop: 12 }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Net Total</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#C9A84C', letterSpacing: '-0.5px' }}>{netTotalFmt}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Duplicate detection modal */}
      {showDupModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 24px 20px', maxWidth: 400, width: '100%', boxShadow: '0 8px 40px rgba(10,22,40,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><AlertTriangle size={24} strokeWidth={1.75} aria-hidden="true" /></div>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0A1628', textAlign: 'center', marginBottom: 6 }}>Possible Duplicate</h3>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', textAlign: 'center', marginBottom: 16, lineHeight: 1.5 }}>
              A similar booking already exists for this date, cruise, and deck:
            </p>
            {dupCandidates.map(b => (
              <div key={b.id} style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0A1628' }}>{b.booking_name}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                  {(b.adults || 0) + (b.children || 0)} pax{b.partner_name ? ` · ${b.partner_name}` : ''}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button
                onClick={() => setShowDupModal(false)}
                style={{ flex: 1, background: '#0A1628', color: '#C9A84C', border: 'none', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Proceed Anyway
              </button>
              <button
                onClick={() => { setShowDupModal(false); setBookingName(''); }}
                style={{ flex: 1, background: 'none', color: 'var(--color-ink-secondary)', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment mismatch warning — blocks save until acknowledged */}
      {payWarningPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 24px 22px', maxWidth: 360, width: '100%', boxShadow: '0 8px 40px rgba(10,22,40,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><AlertTriangle size={24} strokeWidth={1.75} aria-hidden="true" /></div>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0A1628', textAlign: 'center', marginBottom: 8 }}>Payment Mismatch</h3>
            <p style={{ fontSize: '0.84rem', color: '#6b7280', textAlign: 'center', marginBottom: 22, lineHeight: 1.5 }}>
              {payWarning}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={executeSave}
                style={{ flex: 1, background: '#0A1628', color: '#C9A84C', border: 'none', borderRadius: 8, padding: '11px 0', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Save Anyway
              </button>
              <button
                type="button"
                onClick={() => setPayWarningPending(false)}
                style={{ flex: 1, background: 'none', color: 'var(--color-ink-secondary)', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '11px 0', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Fix Amounts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Seating prompt */}
      {seatingPrompt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(10,22,40,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ background:'#ffffff', border:'1px solid var(--color-border)', borderRadius:16, padding:28, maxWidth:380, width:'100%', boxShadow:'0 12px 40px rgba(10,22,40,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}><Armchair size={24} strokeWidth={1.75} aria-hidden="true" /></div>
            <h3 style={{ fontSize:'1rem', fontWeight:800, color:'#0A1628', textAlign:'center', marginBottom:8 }}>
              Booking saved!
            </h3>
            <p style={{ fontSize:'0.85rem', color:'#6a7a8a', textAlign:'center', marginBottom: guestWarning ? 12 : 24, lineHeight:1.5 }}>
              Would you like to assign a table on the Seating Sheet now?
            </p>
            {guestWarning && (
              <div className="toast toast-error" style={{ marginBottom: 20, fontSize: '0.78rem' }}>{guestWarning}</div>
            )}
            <div style={{ display:'flex', gap:10 }}>
              <button
                className="btn btn-gold"
                style={{ flex:1 }}
                onClick={() => onNavigate('seating-sheet', seatingPrompt.date, { cruise_type: seatingPrompt.cruise, highlight_booking_id: seatingPrompt.bookingId })}
              >
                Assign Now
              </button>
              <button
                className="btn btn-outline"
                style={{ flex:1 }}
                onClick={() => {
                  if (seatingPrompt.returnTo === 'daily-tracker') {
                    onNavigate('daily-tracker', seatingPrompt.date, { cruise_type: seatingPrompt.returnCruise });
                  } else {
                    onNavigate(seatingPrompt.returnTo, seatingPrompt.date);
                  }
                }}
              >
                Assign Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
